// #tag: AMBIENT UI SOUND LISTENER
// #purpose: Attaches global event listeners for gesture unlock, button clicks, and incoming notification audio triggers.
import { useEffect, useRef } from 'react';
import { useNotifications } from '../../hooks/useExamData';
import { useUiSound } from '../../hooks/useUiSound';
import { unlockUiSound } from '../../lib/uiSound';

export function UiSoundListener() {
  const { play } = useUiSound();
  const { data: notifData } = useNotifications();
  const prevUnread = useRef<number | null>(null);

  useEffect(() => {
    const unlock = () => unlockUiSound();
    document.addEventListener('pointerdown', unlock, { once: true, passive: true });
    document.addEventListener('keydown', unlock, { once: true, passive: true });
    return () => {
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest('button, [role="button"]');
      if (!button || button.closest('[data-silent]')) return;
      if (button.closest('.wfx-sound-settings')) return;
      if (button.classList.contains('chip-pill') || button.classList.contains('primary-action') || button.classList.contains('secondary-action')) {
        play('click');
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [play]);

  useEffect(() => {
    if (!notifData) return;

    const unread = notifData.meta?.unread ?? 0;
    const latest = notifData.data?.[0];

    if (prevUnread.current !== null && unread > prevUnread.current && latest) {
      if (latest.categoryId === 'result_updates') {
        play('notificationBell');
      } else {
        play('notificationPop');
      }
    }

    prevUnread.current = unread;
  }, [notifData, play]);

  return null;
}
